import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailTemplatesAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import { Mail, Plus, Save, Trash2, RefreshCw, Sparkles, X } from 'lucide-react';
import '../Admin/AdminDashboard.css';

const AdminEmailTemplates = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [filterLang, setFilterLang] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await emailTemplatesAPI.getAll();
      setTemplates(res.data.templates || res.data || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    try {
      await emailTemplatesAPI.seed();
      toast.success('Default templates seeded');
      load();
    } catch {
      toast.error('Seed failed');
    }
  };

  const save = async (tpl) => {
    try {
      if (tpl._id) await emailTemplatesAPI.update(tpl._id, tpl);
      else await emailTemplatesAPI.create(tpl);
      toast.success('Saved');
      setEditing(null); setShowNew(false);
      load();
    } catch {
      toast.error('Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await emailTemplatesAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const filtered = templates.filter(t => filterLang === 'all' || t.language === filterLang);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0a0a0a', color: '#fafafa' }}>
      <AdminSidebar />
      <div style={{ flex: 1, padding: '32px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
              <Mail size={28} color="#f97316" /> Email Templates
            </h1>
            <p style={{ color: '#a1a1aa', marginTop: 4 }}>Reusable bilingual templates with {'{{customer.firstName}}'} variables</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={seed} style={btnGhost}><Sparkles size={16}/> Seed defaults</button>
            <button onClick={load} style={btnGhost}><RefreshCw size={16}/></button>
            <button onClick={() => setShowNew(true)} style={btnPrimary}><Plus size={16}/> New</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['all','it','en'].map(l => (
            <button key={l} onClick={() => setFilterLang(l)}
              style={{ ...chip, background: filterLang === l ? '#f97316' : '#18181b', color: filterLang === l ? '#fff' : '#a1a1aa' }}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        {loading ? <div style={{ color: '#a1a1aa' }}>Loading…</div> : filtered.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', padding: 40, color: '#71717a' }}>
            No templates yet. Click "Seed defaults" to insert 6 starter templates.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
            {filtered.map(t => (
              <div key={t._id} style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: '#71717a', marginTop: 2 }}>
                      <code>{t.key}</code> · {t.language?.toUpperCase()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => setEditing(t)} style={iconBtn} title="Edit">✏️</button>
                    <button onClick={() => remove(t._id)} style={iconBtn} title="Delete"><Trash2 size={14}/></button>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: '#a1a1aa', fontWeight: 500, marginBottom: 6 }}>{t.subject}</div>
                <div style={{ fontSize: 12, color: '#71717a', maxHeight: 80, overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                  {(t.body || '').replace(/<[^>]*>/g,'').slice(0, 200)}…
                </div>
              </div>
            ))}
          </div>
        )}

        {(editing || showNew) && (
          <TemplateEditor
            template={editing || { key:'', name:'', language:'it', subject:'', body:'', variables:[] }}
            onSave={save}
            onCancel={() => { setEditing(null); setShowNew(false); }}
          />
        )}
      </div>
    </div>
  );
};

const TemplateEditor = ({ template, onSave, onCancel }) => {
  const [t, setT] = useState(template);
  return (
    <div style={modalBackdrop} onClick={onCancel}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t._id ? 'Edit' : 'New'} Template</h2>
          <button onClick={onCancel} style={iconBtn}><X size={18}/></button>
        </div>
        <div style={{ display: 'grid', gap: 12 }}>
          <Input label="Key (machine-id)" value={t.key} onChange={v => setT({...t, key:v})} placeholder="quote_followup" />
          <Input label="Name" value={t.name} onChange={v => setT({...t, name:v})} placeholder="Quote Follow-up" />
          <div>
            <label style={lbl}>Language</label>
            <select value={t.language} onChange={e => setT({...t, language:e.target.value})} style={input}>
              <option value="it">Italian</option>
              <option value="en">English</option>
            </select>
          </div>
          <Input label="Subject" value={t.subject} onChange={v => setT({...t, subject:v})} />
          <div>
            <label style={lbl}>Body (HTML, supports {'{{customer.firstName}}'}, {'{{customer.company}}'}, {'{{quote.total}}'})</label>
            <textarea value={t.body} onChange={e => setT({...t, body:e.target.value})}
              style={{ ...input, minHeight: 240, fontFamily: 'monospace', fontSize: 12 }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={onCancel} style={btnGhost}>Cancel</button>
            <button onClick={() => onSave(t)} style={btnPrimary}><Save size={14}/> Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, placeholder }) => (
  <div>
    <label style={lbl}>{label}</label>
    <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={input} />
  </div>
);

const card = { background: '#111', border: '1px solid #27272a', borderRadius: 8, padding: 16 };
const btnPrimary = { background: '#f97316', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 };
const btnGhost = { background: '#18181b', color: '#a1a1aa', border: '1px solid #27272a', padding: '8px 12px', borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 };
const iconBtn = { background: 'transparent', border: '1px solid #27272a', color: '#a1a1aa', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' };
const chip = { padding: '6px 12px', borderRadius: 999, border: '1px solid #27272a', cursor: 'pointer', fontSize: 12, fontWeight: 600 };
const input = { width: '100%', padding: '8px 10px', background: '#18181b', border: '1px solid #27272a', borderRadius: 6, color: '#fafafa', fontSize: 13 };
const lbl = { display: 'block', fontSize: 11, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 };
const modalBackdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };
const modalBox = { background: '#0a0a0a', border: '1px solid #27272a', borderRadius: 10, padding: 24, width: '100%', maxWidth: 720, maxHeight: '90vh', overflow: 'auto' };

export default AdminEmailTemplates;
